import {
  Calendar,
  dateFnsLocalizer,
  type Event,
  type ToolbarProps,
  type View,
} from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { format } from "date-fns/format";
import { parse } from "date-fns/parse";
import { startOfWeek } from "date-fns/startOfWeek";
import { getDay } from "date-fns/getDay";
import { enUS } from "date-fns/locale/en-US";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { Typography } from "../Typography/Typography";

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { "en-US": enUS },
});

interface BigCalendarProps {
  events?: Event[];
  onSelectEvent?: (event: Event) => void;
  eventPropGetter?: (event: Event) => {
    className?: string;
    style?: React.CSSProperties;
    [key: string]: unknown;
  };
}

const CustomToolbar = ({ label, onNavigate, onView, view }: ToolbarProps) => {
  const navigate = (action: "PREV" | "NEXT" | "TODAY") => {
    onNavigate(action);
  };

  const handleViewChange = (newView: "month" | "week") => {
    onView(newView);
  };

  return (
    <div className="mb-3 flex items-center justify-between gap-4">
      {/* Navigation buttons */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            navigate("PREV");
          }}
          aria-label="Previous"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            navigate("NEXT");
          }}
          aria-label="Next"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Date label */}
      <Typography variant="bold" size="lg" className="pt-2">
        {label}
      </Typography>

      {/* View switcher */}
      <div className="bg-card flex items-center gap-1 rounded-md border p-1">
        <Button
          variant={view === "month" ? "default" : "ghost"}
          size="sm"
          onClick={() => {
            handleViewChange("month");
          }}
          className="px-3"
        >
          Month
        </Button>
        <Button
          variant={view === "week" ? "default" : "ghost"}
          size="sm"
          onClick={() => {
            handleViewChange("week");
          }}
          className="px-3"
        >
          Week
        </Button>
      </div>
    </div>
  );
};

/* Currently very specific to litigation dates */
export const BigCalendar = ({
  events = [],
  onSelectEvent,
  eventPropGetter,
}: BigCalendarProps) => {
  const [view, setView] = useState<View>("month");

  return (
    <>
      <div className="h-full">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          view={view}
          onView={setView}
          onSelectEvent={onSelectEvent}
          eventPropGetter={eventPropGetter}
          formats={{
            // For timed events, show a single start time (e.g., "3:00 PM")
            eventTimeRangeFormat: ({ start }) => localizer.format(start, "p"),
          }}
          components={{
            toolbar: CustomToolbar,
          }}
          showAllEvents
        />
      </div>
      <style>{`
      ${
        view === "week"
          ? `
      .rbc-event {
        min-height: 100px !important;
      }
      .rbc-today {
        background-color: transparent !important;
        border-color: inherit !important;
      }
      .rbc-event-content {
        display: -webkit-box !important;
        -webkit-line-clamp: 4 !important;
        -webkit-box-orient: vertical !important;
        overflow: hidden !important;
        white-space: normal !important;
      }
      .rbc-time-view {
        overflow: auto !important;
      }
      .rbc-time-header .rbc-allday-cell {
        max-height: 220px !important;
        overflow-y: auto;
      }
      .rbc-allday-cell::-webkit-scrollbar {
        width: 0px !important;
        height: 0px !important;
      }
      `
          : view === "day"
            ? `
            .rbc-time-view {
        .rbc-label {
          display: none;
        }
        .rbc-allday-cell {
          height: calc(100vh - 98px);
          max-height: unset;
        }
        .rbc-time-content {
          display: none !important;
        }
      }
      .rbc-row-content {
        margin: 8px !important;
      }
      .rbc-event {
        margin-bottom: 2px !important;
      }
      .rbc-event-content {
        display: -webkit-box !important;
        -webkit-line-clamp: 4 !important;
        -webkit-box-orient: vertical !important;
        overflow: hidden !important;
        white-space: normal !important;
      }
      .rbc-time-view {
        overflow: auto !important;
      }
      `
            : `
            `
      }
    `}</style>
    </>
  );
};
